import classNames from 'classnames'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { Field, Form } from 'react-final-form'
import styles from '../../src/components/ticket-display/ticket-sites/ticket-sites.module.scss'
import ticketQuery from '../graphql/queries/ticket'
import sitesQuery from '../graphql/queries/sites'
import QUERY_KEYS from '../graphql/queryKeys'
import useQuery from '../hooks/useQuery'
import { Spinner } from '../pages/shared/Spinner'
import { required } from '../utilities/commonValidators'
import { commaDeliminate } from '../utilities/stringUtilities'
import { captureErrorAndNotify } from '../utilities/errorHandlers'
import addCostToTicketSiteMutation from '../graphql/mutations/addCostToTicketSite'
import addSiteToTicketMutation from '../graphql/mutations/addSiteToTicket'
import removeSiteFromTicketMutation from '../graphql/mutations/removeSiteFromTicket'
import useMutation from '../hooks/useMutation'
import notify from '../utilities/notify'
import TicketSiteItem from './ticket-display/ticket-sites/TicketSiteItem'

export default function SiteEditModal ({ onCancel, siteEditModal, haulerID, refetchTickets }) {
  const [expandedSiteCost, setExpandedSiteCost] = useState([])

  const { data: { sites }, isFetching: isFetchingSites } = useQuery([
    QUERY_KEYS.sites,
    haulerID
  ], sitesQuery, {
    placeholderData: { sites: [] },
    onError (error) {
      captureErrorAndNotify(error, 'Error fetching sites')
    }
  })

  const { data: { ticket }, isFetching: isFetchingTicket, refetch: refetchTicket } = useQuery(
    [QUERY_KEYS.ticket, siteEditModal.ticketId],
    ticketQuery, {
      onError (error) {
        captureErrorAndNotify(error, 'Error loading ticket')
      },
      placeholderData: { ticket: {} }
    })

  const { mutate: addSiteToTicket } = useMutation(addSiteToTicketMutation, {
    onSuccess () {
      notify('success', 'Site Added')
      refetchTicket()
      refetchTickets()
      setExpandedSiteCost([])
    },
    onError (error) {
      captureErrorAndNotify(error, 'Failed to add site to ticket')
    }
  })

  const { mutate: removeSiteFromTicket } = useMutation(removeSiteFromTicketMutation,
    {
      onSuccess () {
        notify('success', 'Site Removed')
        refetchTicket()
        refetchTickets()
        setExpandedSiteCost([])
      },
      onError (error) {
        captureErrorAndNotify(error, 'Error removing site')
      }
    })

  const { mutate: addCostToTicketSite, isLoading: isAddingCostToTicketSite } = useMutation(addCostToTicketSiteMutation, {
    onSuccess () {
      refetchTicket()
      notify('success', 'Cost to Ticket Site Added')
      setExpandedSiteCost([])
    },
    onError (error) {
      captureErrorAndNotify(error, 'Failed to add cost to ticket site')
    }
  })

  function handleSubmit (values, form) {
    form.restart()
    addSiteToTicket({ ...siteEditModal, ...values })
  }

  const nonMatchedSites = useMemo(
    () => sites.filter(singleSite => !ticket?.sites?.some(ticketSite => singleSite.id === ticketSite.id)),
    [ticket, sites]
  )

  if (isFetchingSites) {
    return (<Spinner isFetching />)
  }

  return (
    <div className={classNames('bg-white shadow w-4/6 relative md:-top-1/4', styles.siteEditModalContainer)}>
      <h2 className='p-6'>{'Associated Sites'}</h2>
      <button className={classNames('absolute right-4 top-0', styles.removeBtn)} type='button' onClick={onCancel}>
        <i className={classNames('material-icons')}>close</i>
      </button>
      <div className='p-6'>
        <Form onSubmit={handleSubmit} validate={validate}>
          {({ handleSubmit, invalid }) => (
            <form onSubmit={handleSubmit}>
              <div className='mb-8'>
                <Field name='siteId'>
                  {({ input, meta }) => (<>
                    <label className={classNames('w-full', { 'text-red': meta.invalid && meta.touched })} htmlFor='siteId'>Select Site</label>
                    <select {...input} id='siteId' className='form-control'>
                      <option value={null}></option>
                      {nonMatchedSites.map(tm => {
                        let optionValue = `${tm.name}`
                        if (tm.cost) optionValue += ` - $${commaDeliminate(tm.cost)}`
                        return (
                          <option key={tm.id} value={tm.id}>{`${optionValue}`}</option>
                        )
                      })}
                    </select>
                    <div className={classNames({ 'border-b border-red border-1 relative bottom-1': meta.invalid && meta.touched })}></div>
                    {meta.invalid && meta.touched && <h5>Required</h5>}
                  </>)}
                </Field>
              </div>
              <div>
                {isFetchingTicket && <Spinner isFetching />}
                {!isFetchingTicket && ticket.sites.map((ticketSite, index) => {
                  const siteCostNote = ticket.ticketSiteCostNote?.find((ticketSiteCostObj) => ticketSiteCostObj.site.id === ticketSite.id)
                  const hasSiteCost = Boolean(ticketSite.cost)
                  const siteInfo = {
                    disposalCost: ticketSite.cost,
                    totalCost: siteCostNote?.cost,
                    note: siteCostNote?.notes
                  }
                  return (
                    <TicketSiteItem
                      key={ticketSite.id}
                      ticketSite={ticketSite}
                      index={index}
                      totalSites={ticket.sites.length}
                      siteInfo={siteInfo}
                      hasSiteCost={hasSiteCost}
                      siteCostNote={siteCostNote}
                      expandedSiteCost={expandedSiteCost}
                      setExpandedSiteCost={setExpandedSiteCost}
                      onSubmitCost={({ siteId, cost, notes }) =>
                        addCostToTicketSite({ ...siteEditModal, siteId, cost, notes })
                      }
                      saveDisabled={isAddingCostToTicketSite}
                      onRemove={(siteId) => removeSiteFromTicket({ ...siteEditModal, siteId })}
                    />
                  )
                })}
              </div>
              <div className='flex justify-end mt-4'>
                <button type='submit'
                  className='dis-btn dis-btn-lg dis-btn-primary mr-4'
                  disabled={invalid}>
                  Add
                  <i className='material-icons dis-btn-icon'>check</i>
                </button>
                <button type='button'
                  className='dis-btn dis-btn-lg dis-btn-white'
                  onClick={onCancel}>
                  Close
                  <i className='material-icons dis-btn-icon'>clear</i>
                </button>
              </div>
            </form>
          )}
        </Form>
      </div>
    </div>
  )
}

SiteEditModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  haulerID: PropTypes.string.isRequired,
  siteEditModal: PropTypes.shape({
    ticketId: PropTypes.string.isRequired
  }),
  refetchTickets: PropTypes.func.isRequired
}

function validate (values) {
  return {
    siteId: required(values.siteId)
  }
}
